import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { t } from '@lingui/core/macro';
import { cva, type VariantProps } from 'class-variance-authority';

import ClearIcon from '@/assets/svg/bx-x.svg?react';
import { Button } from '@/components/buttons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'gap-1 border font-medium transition-all inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 focus-visible:ring-2 focus-visible:ring-blue-200 overflow-hidden group/badge',
  {
    variants: {
      isRounded: {
        true: 'rounded-full',
        false: 'rounded-xs',
      },
      color: {
        default: '',
        success: '',
        critical: '',
        warning: '',
        info: '',
      },
      variant: {
        outlined: 'border',
        solid: 'border-transparent',
      },
      size: {
        small:
          'h-5 px-1.5 py-0.5 text-xs has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1',
        medium:
          'h-7 px-2 py-0.5 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
        large:
          'h-8 px-2.5 py-1 text-sm has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        class: 'bg-gray-200! border-gray-200! text-gray-500!',
      },
      {
        color: 'default',
        variant: 'outlined',
        disabled: false,
        class: 'bg-gray-100 border-gray-300 text-gray-900',
      },
      {
        color: 'default',
        variant: 'solid',
        class: 'bg-gray-300 border-transparent text-gray-900',
      },
      {
        color: 'success',
        variant: 'outlined',
        class: 'bg-green-100 border-green-400 text-green-700',
      },
      {
        color: 'success',
        variant: 'solid',
        class: 'bg-green-400 border-transparent text-green-700',
      },
      {
        color: 'critical',
        variant: 'outlined',
        class: 'bg-red-100 border-red-300 text-red-700',
      },
      {
        color: 'critical',
        variant: 'solid',
        class: 'bg-red-300 border-transparent text-red-700',
      },
      {
        color: 'warning',
        variant: 'outlined',
        class: 'bg-yellow-100 border-yellow-400 text-yellow-700',
      },
      {
        color: 'warning',
        variant: 'solid',
        class: 'bg-yellow-200 border-transparent text-yellow-700',
      },
      {
        color: 'info',
        variant: 'outlined',
        class: 'bg-aqua-100 border-aqua-400 text-aqua-800',
      },
      {
        color: 'info',
        variant: 'solid',
        class: 'bg-aqua-300 border-transparent text-aqua-800',
      },
    ],
    defaultVariants: {
      color: 'default',
      variant: 'outlined',
      size: 'medium',
      isRounded: false,
      disabled: false,
    },
  },
);

export type BadgeProps = useRender.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    closable?: boolean;
    onClose?: (e: React.MouseEvent<HTMLElement>) => void;
    showTooltip?: boolean;
    disabled?: boolean;
    closeButtonInverse?: boolean;
  };

const Badge = ({
  className,
  color = 'default',
  variant = 'outlined',
  size = 'medium',
  isRounded = false,
  disabled = false,
  render,
  closable = false,
  onClose,
  showTooltip = true,
  closeButtonInverse = false,
  children,
  ...props
}: BadgeProps) => {
  const closeButton = closable && onClose && (
    <Button
      type="button"
      variant="ghost"
      inverse={closeButtonInverse}
      size="icon-small"
      shape="round"
      data-icon="inline-end"
      aria-label={t`Remove`}
      disabled={disabled}
      className="-mr-0.5 ml-0.5 size-5 min-w-0 p-0"
      onClick={(e) => {
        e.stopPropagation();
        onClose(e);
      }}
    >
      <ClearIcon />
    </Button>
  );

  const badgeContent = useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ className, color, variant, size, isRounded, disabled })),
        'aria-disabled': disabled || undefined,
        children: closeButton ? (
          <>
            {children}
            {closeButton}
          </>
        ) : (
          children
        ),
      },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      color,
      variant,
      size,
      disabled,
    },
  });

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger render={badgeContent} />
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    );
  }

  return badgeContent;
};

export default Badge;
